"use client";

import type { FC } from "react";

import clsx from "clsx";
import { useState } from "react";

import AccordionItems from "./AccordionItems";
import { AccordionSlidesAssetContent } from "./AccordionSlidesAssetContent";
import type { AccordionSlidesProps } from "./AccordionSlidesShared";
import { normalizeAsset } from "./AccordionSlidesShared";

export type { AccordionSlidesItem, AccordionSlidesProps } from "./AccordionSlidesShared";

const MAX_ITEMS = 5;

export const AccordionSlides: FC<AccordionSlidesProps> = ({ title, items, className, subtitle, layout = "spaced" }) => {
  const clippedItems = items.slice(0, MAX_ITEMS);
  const [selectedIdx, setSelectedIdx] = useState(0);

  const selectedItem = clippedItems[selectedIdx];
  const selectedAsset = selectedItem?.asset ? normalizeAsset(selectedItem.asset) : undefined;
  const nextIdx = (selectedIdx + 1) % clippedItems.length;
  const nextAsset = clippedItems[nextIdx]?.asset ? normalizeAsset(clippedItems[nextIdx].asset) : undefined;

  const handleSelect = (idx: number) => setSelectedIdx(idx);

  return (
    <>
      {layout === "center" && (title || subtitle) && (
        <div className='tw-mb-12 tw-flex tw-max-w-xl tw-flex-col tw-gap-4'>
          {title && <h2 className='f-heading-03'>{title}</h2>}
          {subtitle && <p className='f-paragraph-02'>{subtitle}</p>}
        </div>
      )}
      <div
        className={clsx("tw-grid tw-grid-cols-1 tw-gap-8 lg:tw-grid-cols-2 lg:tw-gap-16", className)}
        style={{ perspective: 1200 }}
      >
        {/* Title - row 1 on mobile, row 1 col 1 on desktop */}
        {layout === "spaced" && title && <h2 className='tw-mb-12 lg:tw-mb-0 lg:tw-self-start f-heading-03'>{title}</h2>}

        {/* Asset with background and foreground images - row 2 on mobile, spans both rows in col 2 on desktop */}
        <div
          className={clsx(
            "tw-relative tw-flex tw-items-center tw-justify-end lg:tw-col-start-2 lg:tw-max-w-[600px]",
            title ? "lg:tw-row-span-2 lg:tw-row-start-1" : "lg:tw-row-start-1",
          )}
        >
          <AccordionSlidesAssetContent
            item={selectedItem}
            nextAsset={nextAsset}
            nextIdx={nextIdx}
            selectedAsset={selectedAsset}
            selectedIdx={selectedIdx}
          />
        </div>

        {/* Accordion Items - row 3 on mobile, row 2 col 1 on desktop (spans 2 rows if center layout or no title) */}
        <div
          className={clsx(
            "tw-flex tw-flex-col tw-gap-2 lg:tw-max-w-[600px]",
            layout === "center" ? "tw-justify-center lg:tw-row-span-2" : title ? "lg:tw-self-end" : "lg:tw-self-start",
          )}
        >
          <AccordionItems items={clippedItems} onSelect={handleSelect} selectedIdx={selectedIdx} />
        </div>
      </div>
    </>
  );
};

export default AccordionSlides;
